import { Box, CardContent, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useNotification } from '@revenuewell/front-end-bundle';
import { NoDataComponent } from 'apps/wrapper/src/components/shared/grid/no-data';
import { usePatientProfile } from 'apps/wrapper/src/hooks/use-patient-profile';
import { ContactApi } from 'apps/wrapper/src/services/api-client/contact-api';
import { PatientNote, SecureAttachment } from 'apps/wrapper/src/services/api-client/contact-api.types';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardTitle, Table } from '../../patient-profile-page.styles';
import { Container } from '../notes/notes.styles';
import { AddFileDialog } from 'apps/wrapper/src/components/layout/slideout/patient-profile-widget/views/files/add-file/add-file';
import PatientFilesTableSkeleton from './patient-files-skeleton';

export const PatientFilesView: FC = () => {
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();

  const [files, setFiles] = useState<SecureAttachment[]>([]);
  const [selectedFile, setSelectedFile] = useState<SecureAttachment>();
  const [fileDialogOpened, setFileDialogOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedNote, setSelectedNote] = useState<PatientNote>();
  const [noteDialogOpened, setNoteDialogOpened] = useState(false);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  const loadFiles = useCallback(async () => {
    setIsLoading(true);

    try {
      const filesData = await contactApi.getSecureAttachments(contactDetails.id, locationId);
      setFiles(filesData);
    } catch {
      showError('Unable to load patient files.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const closeFileDialog = useCallback(() => {
    loadFiles();
    setFileDialogOpened(false);
    setSelectedFile(undefined);
  }, []);

  const handleFileClick = useCallback((file: SecureAttachment) => {
    setSelectedFile(file);
    setFileDialogOpened(true);
  }, []);

  const handleAddFileClick = useCallback(() => {
    setFileDialogOpened(true);
  }, []);

  const handleFileChange = useCallback(() => {
    loadFiles();
    closeFileDialog();
  }, [loadFiles]);

  return (
    <>
      <Container>
        <Card>
          <CardContent>
            <CardTitle sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px', width: 'auto' }}>
              <Box>Patient Files</Box>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button size="small" onClick={handleAddFileClick} variant="contained" disableElevation>
                  Add File
                </Button>
              </Box>
            </CardTitle>
            {isLoading && <PatientFilesTableSkeleton />}
            {!isLoading && files.length === 0 && <NoDataComponent />}
            {!isLoading && files.length > 0 && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '15%' }}>Date</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>User</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => (
                    <TableRow key={`patient-files-${file.fileName}`}>
                      <TableCell style={{ width: '15%', paddingRight: '15px', color: 'black' }}>
                        {file.dateCreated ? moment(file.dateCreated).format('MM/DD/YYYY') : null}
                      </TableCell>
                      <TableCell onClick={() => handleFileClick(file)} style={{ paddingRight: '15px' }}>
                        <Box sx={{ color: '#0675DD', '&:hover': { cursor: 'pointer', textDecoration: 'underline' } }}>
                          {file.displayName || '-'}
                        </Box>
                      </TableCell>
                      <TableCell>{file.description}</TableCell>
                      <TableCell>{file.addedByFullName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Container>
      <AddFileDialog
        opened={fileDialogOpened}
        attachment={selectedFile}
        patientId={contactDetails.id}
        locationName={contactDetails?.location?.name}
        onClose={closeFileDialog}
        onChange={handleFileChange}
      />
    </>
  );
};
